// The spec: what the model decides about a game before writing any code. Produced as
// structured output (the JSON schema below), shown to the user as a card, patched by edits,
// and the source the validator derives its checks from (which sprites must resolve, which
// score field must exist). Small and in the game's own words; the code is the detail.

export const KINDS = ["grow", "side", "dig", "stations", "shared", "chase"] as const;
export type KindId = (typeof KINDS)[number];

export const STYLES = ["pixel-16", "pixel-18", "vector", "shapes"] as const;
export type StyleId = (typeof STYLES)[number];

export const ROLES = ["player", "npc", "enemy", "item", "scenery", "projectile", "hazard", "shared"] as const;

export interface SpecEntity {
  type: string;
  role: (typeof ROLES)[number];
  /** One sentence: what it does each tick, or that it does nothing. */
  behaviour: string;
  /** Server-only and replicated fields beyond x, y, r, each with a one-word type. */
  fields: { name: string; type: "number" | "string" | "boolean"; replicated: boolean }[];
  static: boolean;
  /** A keyword for the sprite library, or empty for a drawn shape. */
  sprite: string;
}

export interface Spec {
  kind: KindId;
  title: string;
  /** Lower-case letters, digits and hyphens; the catalogue key and the share link. */
  slug: string;
  /** One line for the feed card. */
  blurb: string;
  world: { w: number; h: number };
  players: { max: number };
  /** What A and B do, one line, for the controls card. */
  controls: string;
  look: {
    style: StyleId;
    /** Up to three colours, CSS hex, for drawn parts. */
    palette: string[];
  };
  buttons: { a: string; b: string };
  entities: SpecEntity[];
  /** The rules, one per line, each checkable by watching the game. */
  rules: string[];
  /** The replicated numeric field on the player entity the leaderboard ranks by, or empty. */
  score: { field: string; means: string };
}

/** JSON schema for OpenRouter structured output (strict: every property required, no extras). */
export const SPEC_SCHEMA = {
  name: "game_spec",
  strict: true,
  schema: {
    type: "object",
    additionalProperties: false,
    required: ["kind", "title", "slug", "blurb", "world", "players", "controls", "look", "buttons", "entities", "rules", "score"],
    properties: {
      kind: { type: "string", enum: [...KINDS], description: "The kind of game, by mechanics: grow (eat and grow, top-down), side (side view with gravity), dig (a grid of blocks to dig and place), stations (top-down walk-about with places to work and NPCs), shared (one object everyone operates together), chase (tag, infection, hunting other players, top-down)." },
      title: { type: "string", description: "Two or three words, its own name, not a copy of a known game's." },
      slug: { type: "string", pattern: "^[a-z0-9][a-z0-9-]{1,30}$" },
      blurb: { type: "string", description: "One line for the feed card, under 110 characters." },
      world: { type: "object", additionalProperties: false, required: ["w", "h"], properties: { w: { type: "integer", minimum: 1200, maximum: 8000 }, h: { type: "integer", minimum: 800, maximum: 8000 } } },
      players: { type: "object", additionalProperties: false, required: ["max"], properties: { max: { type: "integer", minimum: 2, maximum: 64 } } },
      controls: { type: "string", description: "One line: what A and B do here." },
      look: {
        type: "object",
        additionalProperties: false,
        required: ["style", "palette"],
        properties: {
          style: { type: "string", enum: [...STYLES], description: "One style for the whole game. pixel-16: Tiny Dungeon and Tiny Town top-down tiles and folk. pixel-18: Pixel Platformer side-view tiles, aliens and robots. vector: space ships and top-down people with guns. shapes: no sprites, everything drawn." },
          palette: { type: "array", minItems: 1, maxItems: 3, items: { type: "string", pattern: "^#[0-9a-fA-F]{6}$" } },
        },
      },
      buttons: { type: "object", additionalProperties: false, required: ["a", "b"], properties: { a: { type: "string" }, b: { type: "string" } } },
      entities: {
        type: "array",
        minItems: 1,
        maxItems: 12,
        items: {
          type: "object",
          additionalProperties: false,
          required: ["type", "role", "behaviour", "fields", "static", "sprite"],
          properties: {
            type: { type: "string", pattern: "^[a-z][a-z0-9-]{0,24}$" },
            role: { type: "string", enum: [...ROLES] },
            behaviour: { type: "string" },
            fields: {
              type: "array",
              maxItems: 16,
              items: {
                type: "object",
                additionalProperties: false,
                required: ["name", "type", "replicated"],
                properties: { name: { type: "string", pattern: "^[a-zA-Z][a-zA-Z0-9]{0,15}$" }, type: { type: "string", enum: ["number", "string", "boolean"] }, replicated: { type: "boolean" } },
              },
            },
            static: { type: "boolean" },
            sprite: { type: "string", description: "A keyword for the sprite library in the chosen style, or an empty string for a drawn shape." },
          },
        },
      },
      rules: { type: "array", minItems: 2, maxItems: 12, items: { type: "string" } },
      score: { type: "object", additionalProperties: false, required: ["field", "means"], properties: { field: { type: "string" }, means: { type: "string" } } },
    },
  },
} as const;

/** Check a parsed object against the parts of the schema a game depends on. Returns problems, empty when fine. */
export function checkSpec(v: unknown): string[] {
  const p: string[] = [];
  const s = v as Partial<Spec> | null;
  if (!s || typeof s !== "object") return ["spec is not an object"];
  if (!KINDS.includes(s.kind as KindId)) p.push(`kind must be one of ${KINDS.join(", ")}`);
  if (typeof s.title !== "string" || !s.title.trim()) p.push("title is missing");
  if (typeof s.slug !== "string" || !/^[a-z0-9][a-z0-9-]{1,30}$/.test(s.slug)) p.push("slug must be lower-case letters, digits and hyphens");
  if (typeof s.blurb !== "string" || !s.blurb.trim()) p.push("blurb is missing");
  if (!s.world || !(s.world.w >= 1200) || !(s.world.h >= 800)) p.push("world must be at least 1200 x 800");
  if (!s.players || !(s.players.max >= 2)) p.push("players.max must be at least 2");
  if (typeof s.controls !== "string" || !s.controls.trim()) p.push("controls line is missing");
  if (!s.look || !STYLES.includes(s.look.style as StyleId)) p.push(`look.style must be one of ${STYLES.join(", ")}`);
  if (!s.look || !Array.isArray(s.look.palette) || s.look.palette.length === 0) p.push("look.palette needs at least one colour");
  if (!s.buttons || typeof s.buttons.a !== "string" || typeof s.buttons.b !== "string") p.push("buttons.a and buttons.b are missing");
  if (!Array.isArray(s.entities) || s.entities.length === 0) p.push("entities is empty");
  else {
    const types = new Set<string>();
    let player = 0;
    for (const e of s.entities) {
      if (!e || typeof e.type !== "string") {
        p.push("an entity has no type");
        continue;
      }
      if (types.has(e.type)) p.push(`entity type "${e.type}" is listed twice`);
      types.add(e.type);
      if (e.role === "player") player++;
      if (!ROLES.includes(e.role)) p.push(`entity "${e.type}" has an unknown role`);
    }
    if (player !== 1) p.push("exactly one entity must have the role player");
  }
  if (!Array.isArray(s.rules) || s.rules.length < 2) p.push("rules needs at least two lines");
  if (!s.score || typeof s.score.field !== "string") p.push("score.field is missing (use an empty string for no leaderboard)");
  return p;
}
